import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

import {
  ENERGY_SQPP_SLUGS,
  ENERGY_SQPP_SLUG_PATTERN
} from '~~/shared/energy-sqpp-slugs'

dayjs.extend(isBetween)

/**
 * HI-1430 — Energy single-question-per-page (SQPP) lead funnel.
 *
 * The Optimizely variant of `/energy/quote/form/step1..step8`. One question per
 * screen, icon-led answers, conditional branching.
 *
 * IMPORTANT — this table is a *view* onto the control's store buckets, not a new
 * storage shape. Every row declares the `stepN` bucket and `field` the control
 * already writes, so `leadParams`, `productRequest` and `has_comparison` all keep
 * working unchanged and a visitor can move between arms mid-session.
 *
 * Do NOT edit `~/constants/funnel/energy.js` for this experiment — the control
 * funnel and `[step].online-comparison-questions.test.ts` depend on it verbatim.
 */

export const SQPP_URL = '/energy/quote/form'

/**
 * Provider logos for the `what-to-compare` hero screen — the "Our energy
 * providers" grid, 3-up on mobile and 5-up on desktop per the Figma.
 *
 * `V2MoleculesCardImageCard` prefers `company.image` over
 * `assetsBaseUrl + company`, which is how Energy Australia can come off the
 * hosted CDN while the rest resolve from public/icons/energy/company-logos/.
 */
export const SQPP_PROVIDER_LOGOS = [
  { company: 'OVO.svg', image: 'https://asset.compareclub.com.au/energy/partners/OVO.svg' },
  { company: '1st-energy.svg' },
  { company: 'Sumo.svg' },
  {
    company: 'ENERGY-AUSTRALIA.svg',
    image: 'https://asset.compareclub.com.au/energy/partners/ENERGY-AUSTRALIA.svg'
  },
  { company: 'Engie.svg' },
  { company: 'Dodo.svg' },
  { company: 'AGL.svg' },
  { company: 'ORIGIN.svg' },
  { company: 'POWERSHOP.svg', image: 'https://asset.compareclub.com.au/energy/partners/POWERSHOP.svg' },
  { company: 'MOMENTUM.svg', image: 'https://asset.compareclub.com.au/energy/partners/MOMENTUM.svg' }
]

/**
 * Logo grid for the retailer screens — the Figma's 10 providers, in Figma
 * order: OVO / 1st Energy / Sumo / Energy Australia / Engie, then Dodo / AGL /
 * Origin / Powershop / Momentum. Rendered 5-up on desktop and 3-up on mobile,
 * which is what `gridColumns: 5` resolves to in `ProviderLogoSelection`.
 *
 * Alinta and Red Energy were dropped — they are not in the design. Anything
 * not in this grid is still reachable through the searchable dropdown
 * underneath, which is driven by `getCurrentRetailers()`.
 *
 * The mobile frames show only 9 (Momentum omitted). Treated as an export
 * artefact and rendered on both breakpoints — a provider silently unavailable
 * on phones is worse than a tenth tile wrapping to a fourth row.
 *
 * All ten resolve today: the six on the hosted CDN plus 1st Energy, Sumo and
 * Dodo from public/icons/energy/company-logos/. OVO, Powershop and Momentum
 * were assumed missing during shaping but are present on
 * asset.compareclub.com.au — verified by rendering the live route.
 *
 * LOAD-BEARING: `name` (and `code`) must be a VERBATIM key of
 * `/api/energy/current-retailers`, which is what the page feeds the dropdown
 * as `dataArr`. `ProviderLogoSelection` writes the provider object straight to
 * the model, and `MoleculesSelectDropdown` only renders an input value it can
 * find back in `dataArr` (`_selected` -> `checkSelected`, matched on
 * `dataInputDisplay`, default 'name'). A tile whose name is not in that list
 * resolves to `undefined`, which the `inputText` watcher then emits back up as
 * an empty model — so the click silently clears the answer instead of filling
 * the field. That is what broke OVO / EnergyAustralia / Origin, which the API
 * returns without the word "Energy".
 *
 * The API also buckets: it returns both 'Momentum' -> 'Momentum' and
 * 'Momentum Energy' -> 'Other'. The tile carries the Momentum logo, so it must
 * send the canonical 'Momentum' or the CRM files the lead under Other. Same
 * reason 'Dodo' is not 'Dodo Power & Gas'.
 */
export const SQPP_RETAILER_PROVIDERS = [
  {
    code: 'OVO',
    name: 'OVO',
    logo: 'https://asset.compareclub.com.au/energy/partners/OVO.svg',
    dimensions: { width: 60, height: 40 }
  },
  {
    code: '1st Energy',
    name: '1st Energy',
    logo: '/icons/energy/company-logos/1st-energy.svg',
    dimensions: { width: 80, height: 40 }
  },
  {
    code: 'SUMO',
    name: 'SUMO',
    logo: '/icons/energy/company-logos/Sumo.svg',
    dimensions: { width: 76, height: 30 }
  },
  {
    code: 'EnergyAustralia',
    name: 'EnergyAustralia',
    logo: 'https://asset.compareclub.com.au/energy/partners/ENERGY-AUSTRALIA.svg',
    dimensions: { width: 100, height: 100 }
  },
  {
    code: 'Engie',
    name: 'Engie',
    logo: 'https://asset.compareclub.com.au/energy/partners/ENGIE.svg',
    dimensions: { width: 87, height: 23 }
  },
  {
    code: 'Dodo',
    name: 'Dodo',
    logo: '/icons/energy/company-logos/Dodo.svg',
    dimensions: { width: 80, height: 30 }
  },
  {
    code: 'AGL',
    name: 'AGL',
    logo: 'https://asset.compareclub.com.au/energy/partners/AGL.svg',
    dimensions: { width: 53, height: 50 }
  },
  {
    code: 'Origin',
    name: 'Origin',
    logo: 'https://asset.compareclub.com.au/energy/partners/ORIGIN.svg',
    dimensions: { width: 40, height: 40 }
  },
  {
    code: 'Powershop',
    name: 'Powershop',
    logo: 'https://asset.compareclub.com.au/energy/partners/POWERSHOP.svg',
    dimensions: { width: 90, height: 30 }
  },
  {
    code: 'Momentum',
    name: 'Momentum',
    logo: 'https://asset.compareclub.com.au/energy/partners/MOMENTUM.svg',
    dimensions: { width: 80, height: 40 }
  }
]

// Stage labels fed straight into V2AtomsHorizontalStepper.
export const SQPP_STAGE = {
  ABOUT_YOU: 'About You',
  ENERGY_DETAILS: 'Energy Details'
}

/**
 * Stepper labels for the VARIANT — deliberately not the control's `FORM_STEPS`.
 *
 * The control (`energy.js`) runs ['About You', 'Energy Details', ...]. The
 * Figma runs the reverse: 19 of the 21 informative frames draw stage 1 as
 * "Energy Details" and stage 2 as "About You", and the two that disagree
 * (desktop 02/03) are contradicted by the MOBILE export of the same screen —
 * which is what identifies them as the buggy pair rather than the spec.
 *
 * It lives here, not in `energy.js`, because the control funnel must stay
 * byte-identical for the A/B — see the spec's "Control is provably untouched".
 * `V2AtomsHorizontalStepper` resolves a string `modelValue` against
 * `step.label || step`, so the page only has to import this instead.
 */
export const SQPP_FORM_STEPS = [
  SQPP_STAGE.ENERGY_DETAILS,
  SQPP_STAGE.ABOUT_YOU,
  'Compare',
  'Apply'
]

// Stored values, never the design labels. `leadParams` maps `account_type`
// again on the way out ('SME' -> 'Business'), so storing the label 'Business'
// here would silently file every business lead as Residential.
export const SQPP_VALUE = {
  FUEL_ELECTRICITY: 'Electricity',
  FUEL_GAS: 'Gas',
  FUEL_DUAL: 'Dual',
  ACCOUNT_RESIDENTIAL: 'Residential',
  ACCOUNT_SME: 'SME',
  SALES_MOVE_IN: 'Move In',
  SALES_BETTER_DEAL: 'Better Deal'
}

/**
 * The flow table.
 *
 * `showIf(model)` receives the FLATTENED model (`flattenObj(form, 1)`), so it
 * reads bare field names. `next`/`prev` are derived by filtering this table
 * through `showIf` — skipped rows are simply not in the resulting array, so
 * Back can never land on a question the visitor did not see.
 *
 * `clearOnLeave` lists extra sibling fields to wipe when the row drops off the
 * path — `has_comparison` is the load-bearing one: a stale `true` carried from
 * a Home answer into a Business lead would route them into a comparison they
 * are structurally excluded from.
 *
 * `icon` names a v2 icon the page draws inside the right edge of a text
 * field — the Figma's resting glyph. It hides once the answer is valid, which
 * is when the field's own success tick takes that spot.
 */
export const SQPP_FLOW = [
  {
    slug: 'what-to-compare',
    step: 'step1',
    field: 'fuel_type',
    // Stage 1. Never actually rendered — the hero layout has no stepper — but
    // kept consistent with the re-mapped model so `stage` is never misleading.
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'What would you like to compare?',
    autoAdvance: true,
    showIf: () => true,
    // The funnel's entry screen is a HERO, not the centred question card: a
    // purple full-bleed band with the Trustpilot strip, the display headline,
    // then a light card holding the tiles and the provider grid. Structure and
    // token stack are lifted from the shipped equivalent at
    // app/pages/health-insurance/quote/form/step0.vue. No stepper, no Back.
    layout: 'hero',
    hero: {
      headline: 'Are you paying over <span class="text-semantic-inverse-primary">$600 every quarter</span> on your energy bills?',
      subheading: 'We might be able to help. Compare energy plans from our panel <span class="text-semantic-inverse-primary">in minutes.</span>',
      providersHeading: 'Our energy providers'
    },
    component: 'V2AtomsSelection',
    props: {
      name: 'fuel_type',
      type: 'radio',
      ticker: 'none',
      direction: 'row',
      imagePosition: 'top',
      size: 'small',
      // 3-up on desktop, 2-up on mobile (the third tile wraps to its own row),
      // matching the two Figma frames.
      cols: 3,
      mobileCols: 2,
      dataTestid: 'sqpp-what-to-compare-selection',
      options: [
        {
          label: 'Electricity',
          value: SQPP_VALUE.FUEL_ELECTRICITY,
          name: 'electricity',
          image: '/icons/energy/quick-compare/sqpp-electricity.svg',
          imageWidth: 40,
          imageHeight: 40
        },
        {
          label: 'Gas',
          value: SQPP_VALUE.FUEL_GAS,
          name: 'gas',
          image: '/icons/energy/quick-compare/sqpp-gas.svg',
          imageWidth: 40,
          imageHeight: 40
        },
        {
          label: 'Electricity & Gas',
          value: SQPP_VALUE.FUEL_DUAL,
          name: 'dual',
          image: '/icons/energy/quick-compare/electricity-and-gas.svg',
          imageWidth: 40,
          imageHeight: 40
        }
      ]
    }
  },
  {
    slug: 'home-or-business',
    step: 'step3',
    field: 'account_type',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'Is this for your home or your business?',
    autoAdvance: true,
    showIf: () => true,
    component: 'V2AtomsSelection',
    // The Figma draws two ~166px tiles centred, not two stretched halves of the
    // card. `cols: 2` alone stretches each to ~350px on desktop; capping the
    // wrapper at 22rem lands them on 166px and is a no-op on mobile, where the
    // ~311px card interior is already narrower and yields the ~148px drawn.
    maxWidthClass: 'max-w-[22rem]',
    props: {
      name: 'account_type',
      type: 'radio',
      ticker: 'none',
      direction: 'row',
      imagePosition: 'top',
      size: 'small',
      cols: 2,
      mobileCols: 2,
      dataTestid: 'sqpp-home-or-business-selection',
      options: [
        {
          label: 'Home',
          value: SQPP_VALUE.ACCOUNT_RESIDENTIAL,
          name: 'residential',
          image: '/icons/energy/quick-compare/sqpp-home.svg',
          imageWidth: 40,
          imageHeight: 40
        },
        {
          label: 'Business',
          value: SQPP_VALUE.ACCOUNT_SME,
          name: 'sme',
          image: '/icons/energy/quick-compare/sqpp-business.svg',
          imageWidth: 40,
          imageHeight: 40
        }
      ]
    }
  },
  {
    slug: 'moving',
    step: 'step2',
    field: 'sales_type',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'Are you moving into a new place?',
    autoAdvance: true,
    showIf: () => true,
    component: 'V2AtomsSelection',
    maxWidthClass: 'max-w-[22rem]',
    props: {
      name: 'sales_type',
      type: 'radio',
      ticker: 'none',
      direction: 'row',
      imagePosition: 'top',
      size: 'small',
      cols: 2,
      mobileCols: 2,
      dataTestid: 'sqpp-moving-selection',
      options: [
        {
          label: 'Yes',
          value: SQPP_VALUE.SALES_MOVE_IN,
          name: 'move_in',
          image: '/icons/ThumbsUp.svg',
          imageWidth: 40,
          imageHeight: 40
        },
        {
          label: 'No',
          value: SQPP_VALUE.SALES_BETTER_DEAL,
          name: 'better_deal',
          image: '/icons/ThumbsDown.svg',
          imageWidth: 40,
          imageHeight: 40
        }
      ]
    }
  },
  {
    slug: 'moving-date',
    step: 'step2',
    field: 'moving_date',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'Which day are you moving?',
    autoAdvance: false,
    showIf: model => model?.sales_type === SQPP_VALUE.SALES_MOVE_IN,
    component: 'AtomsDatepicker',
    maxWidthClass: 'max-w-[22rem]',
    props: {
      name: 'moving_date',
      label: 'Please enter moving date',
      placeholder: 'DD/MM/YYYY',
      disableDatepicker: false,
      theme: 'light',
      modelType: 'date',
      disableDate: date => {
        return !dayjs().isBetween(dayjs(date).add(1, 'day'), dayjs(date).subtract(60, 'day'))
      },
      'data-testid': 'sqpp-moving-date-input'
    }
  },
  {
    slug: 'own-or-rent',
    step: 'step4',
    field: 'property_type',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'Do you own the property, or are you renting?',
    autoAdvance: true,
    // Deliberate delta from control, per Appendix C: business paths keep this
    // question. Control wipes `property_type` on any account-type change.
    showIf: () => true,
    component: 'V2AtomsSelection',
    maxWidthClass: 'max-w-[22rem]',
    props: {
      name: 'property_type',
      type: 'radio',
      ticker: 'none',
      direction: 'row',
      imagePosition: 'top',
      size: 'small',
      cols: 2,
      mobileCols: 2,
      dataTestid: 'sqpp-own-or-rent-selection',
      options: [
        {
          label: 'Own',
          value: 'Own',
          name: 'own',
          image: '/icons/energy/quick-compare/key.svg',
          imageWidth: 40,
          imageHeight: 40
        },
        {
          // The Figma draws stacked coins, which already ships as Coins.svg.
          label: 'Rent',
          value: 'Rent',
          name: 'rent',
          image: '/icons/Coins.svg',
          imageWidth: 40,
          imageHeight: 40
        }
      ]
    }
  },
  {
    slug: 'address',
    step: 'step7',
    field: 'address',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'What\'s the address we should use?',
    // No `desc` — the Figma draws the question then the input, nothing between.
    autoAdvance: false,
    showIf: model => model?.account_type === SQPP_VALUE.ACCOUNT_RESIDENTIAL,
    // MSAT verification writes these on the same bucket. If the visitor later
    // switches to Business the address row leaves the path, and a stale
    // `has_comparison: true` would follow them into post-lead routing.
    clearOnLeave: ['msat_uid', 'selected_nmi', 'has_comparison', 'msat_status'],
    maxWidthClass: 'max-w-[22rem]',
    component: 'MoleculesFormGoogleAddress',
    icon: 'V2IconsMagnifyingGlass',
    props: {
      placeholder: 'Start typing your address',
      name: 'energy_residential_address',
      label: 'Address',
      state: '',
      // The magnifying glass replaces the dropdown chevron.
      hideDropdownIcon: true,
      'data-testid': 'sqpp-address-input'
    }
  },
  {
    slug: 'postcode',
    step: 'step7',
    field: 'postcode',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'What\'s your business\'s postcode or suburb?',
    // No `desc` — see the address row.
    autoAdvance: false,
    showIf: model => model?.account_type === SQPP_VALUE.ACCOUNT_SME,
    maxWidthClass: 'max-w-[22rem]',
    component: 'MoleculesPostcode',
    icon: 'V2IconsMapPin',
    props: {
      label: 'Postcode or suburb',
      'data-testid': 'sqpp-postcode-input'
    }
  },
  {
    slug: 'business-name',
    step: 'step7',
    field: 'business_name',
    // The only two stage-2 rows: the Figma flips the stepper to "About You"
    // for the business-name and contact-details captures.
    stage: SQPP_STAGE.ABOUT_YOU,
    question: 'What\'s your business name?',
    autoAdvance: false,
    showIf: model => model?.account_type === SQPP_VALUE.ACCOUNT_SME,
    maxWidthClass: 'max-w-[22rem]',
    component: 'AtomsInput',
    icon: 'V2IconsBriefcase',
    props: {
      name: 'business_name',
      placeholder: '',
      label: '',
      theme: 'light',
      'data-testid': 'sqpp-business-name-input'
    }
  },
  {
    slug: 'usage',
    step: 'step8',
    field: 'energy_usage_estimate',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'How much energy does your household use?',
    // No `desc` — the Figma goes straight from the question to the three rows.
    autoAdvance: true,
    showIf: model => model?.account_type === SQPP_VALUE.ACCOUNT_RESIDENTIAL,
    component: 'V2AtomsSelection',
    // The Figma rows measure 543px inside the card, not the full ~670px.
    maxWidthClass: 'max-w-[34rem]',
    props: {
      name: 'energy_usage_estimate',
      type: 'radio',
      ticker: 'none',
      // `imagePosition: 'side'` renders a leading image cell plus a
      // `font-normal text-sm` description line under the bolded label.
      imagePosition: 'side',
      dataTestid: 'sqpp-usage-selection',
      // Descriptions are plain strings on purpose. They previously wrapped in
      // `text-semantic-surface-on-surface-variant`, which is the known AA
      // failure (#8C8C89, 3.20:1 at text-sm); the Figma draws normal body text,
      // so adopting the design also clears the contrast defect.
      options: [
        {
          label: 'Low',
          value: 'small',
          name: 'low',
          description: 'Small home or 1–2 occupants with light everyday usage.',
          image: '/icons/energy/quick-compare/battery-low.svg',
          imageWidth: 40,
          imageHeight: 40
        },
        {
          label: 'Medium',
          value: 'medium',
          name: 'medium',
          description: 'Typical family home or 3–4 occupants with standard energy needs.',
          image: '/icons/energy/quick-compare/battery-medium.svg',
          imageWidth: 40,
          imageHeight: 40
        },
        {
          label: 'High',
          value: 'large',
          name: 'high',
          description: 'Large family, 5+ occupants, or heavy energy consumption.',
          image: '/icons/energy/quick-compare/battery-full.svg',
          imageWidth: 40,
          imageHeight: 40
        }
      ]
    }
  },
  {
    slug: 'electricity-retailer',
    step: 'step8',
    field: 'electricity_retailer',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'Who’s your current electricity provider?',
    desc: 'Select your provider below or search for another one.',
    autoAdvance: false,
    skippable: true,
    skipLabel: 'Not sure? Skip for now',
    showIf: model =>
      model?.account_type === SQPP_VALUE.ACCOUNT_RESIDENTIAL &&
      model?.sales_type !== SQPP_VALUE.SALES_MOVE_IN &&
      model?.fuel_type !== SQPP_VALUE.FUEL_GAS,
    component: 'OrganismsProviderLogoSelection',
    props: {
      name: 'electricity_retailer',
      label: 'Type or select a provider',
      theme: 'light',
      dataArr: [],
      searchable: true,
      required: true,
      errorMsg: 'Please select the electricity retailer name',
      gridColumns: 5,
      gridGap: 'gap-4',
      borderVariant: 'variant',
      dropdownSize: 'compact',
      dividerVariant: 'compact',
      dividerText: 'or',
      containerBackground: 'bg-semantic-surface-container-lowest',
      providers: SQPP_RETAILER_PROVIDERS,
      'data-testid': 'sqpp-electricity-retailer-input'
    }
  },
  {
    slug: 'gas-retailer',
    step: 'step8',
    field: 'gas_retailer',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'Who’s your current gas provider?',
    desc: 'Select your provider below or search for another one.',
    autoAdvance: false,
    skippable: true,
    skipLabel: 'Not sure? Skip for now',
    showIf: model =>
      model?.account_type === SQPP_VALUE.ACCOUNT_RESIDENTIAL &&
      model?.sales_type !== SQPP_VALUE.SALES_MOVE_IN &&
      model?.fuel_type !== SQPP_VALUE.FUEL_ELECTRICITY,
    component: 'OrganismsProviderLogoSelection',
    props: {
      name: 'gas_retailer',
      label: 'Type or select a provider',
      theme: 'light',
      dataArr: [],
      searchable: true,
      required: true,
      errorMsg: 'Please select the gas retailer name',
      gridColumns: 5,
      gridGap: 'gap-4',
      borderVariant: 'variant',
      dropdownSize: 'compact',
      dividerVariant: 'compact',
      dividerText: 'or',
      containerBackground: 'bg-semantic-surface-container-lowest',
      providers: SQPP_RETAILER_PROVIDERS,
      'data-testid': 'sqpp-gas-retailer-input'
    }
  },
  {
    slug: 'life-support',
    step: 'step8',
    field: 'life_support',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'Do you have any life-support equipment at your place?',
    desc: 'This helps us protect your essential power supply during outages.',
    autoAdvance: true,
    showIf: model => model?.account_type === SQPP_VALUE.ACCOUNT_RESIDENTIAL,
    component: 'V2AtomsSelection',
    maxWidthClass: 'max-w-[22rem]',
    props: {
      name: 'life_support',
      type: 'radio',
      ticker: 'none',
      direction: 'row',
      imagePosition: 'top',
      size: 'small',
      cols: 2,
      mobileCols: 2,
      dataTestid: 'sqpp-life-support-selection',
      // `life_support` passes through leadParams RAW — it must stay the
      // 'Yes'/'No' string the control stores, never a boolean.
      options: [
        {
          label: 'Yes',
          value: 'Yes',
          name: 'yes',
          image: '/icons/ThumbsUp.svg',
          imageWidth: 40,
          imageHeight: 40
        },
        {
          label: 'No',
          value: 'No',
          name: 'no',
          image: '/icons/ThumbsDown.svg',
          imageWidth: 40,
          imageHeight: 40
        }
      ]
    }
  },
  {
    slug: 'solar',
    step: 'step8',
    field: 'solar',
    stage: SQPP_STAGE.ENERGY_DETAILS,
    question: 'Do you have solar panels at home?',
    autoAdvance: true,
    // Control's rule: gas leads get life support but not solar.
    showIf: model =>
      model?.account_type === SQPP_VALUE.ACCOUNT_RESIDENTIAL &&
      model?.fuel_type !== SQPP_VALUE.FUEL_GAS,
    component: 'V2AtomsSelection',
    maxWidthClass: 'max-w-[22rem]',
    props: {
      name: 'solar',
      type: 'radio',
      ticker: 'none',
      direction: 'row',
      imagePosition: 'top',
      size: 'small',
      cols: 2,
      mobileCols: 2,
      dataTestid: 'sqpp-solar-selection',
      // leadParams coerces `solar` to the STRINGS 'true'/'false' via
      // `formData?.solar === 'Yes'`. A boolean here turns a Yes into 'false'.
      options: [
        {
          label: 'Yes',
          value: 'Yes',
          name: 'yes',
          image: '/icons/ThumbsUp.svg',
          imageWidth: 40,
          imageHeight: 40
        },
        {
          label: 'No',
          value: 'No',
          name: 'no',
          image: '/icons/ThumbsDown.svg',
          imageWidth: 40,
          imageHeight: 40
        }
      ]
    }
  },
  {
    // Contact fields live at the TOP level of the form store, not in a stepN
    // bucket — same as the control's details page. `step`/`field` are null and
    // the page renders the dedicated contact component instead of `component`.
    slug: 'contact-details',
    step: null,
    field: null,
    // Stage 2. The Figma flips the stepper to "About You" for the personal
    // capture, and every contact-details frame on both breakpoints draws the
    // first stage complete. With `business-name` this is one of only two
    // stage-2 rows, so no path can reach the lead without the stepper advancing.
    stage: SQPP_STAGE.ABOUT_YOU,
    question: 'We just need a few more details!',
    autoAdvance: false,
    showIf: () => true,
    component: 'V2MoleculesEnergyContactDetailsForm',
    contactFields: ['full_name', 'email', 'phone'],
    props: {}
  }
]

// Re-exported from shared/ so nuxt.config.ts can constrain the route without
// pulling dayjs into the config load. The flow table asserts they agree.
export const SQPP_SLUGS = ENERGY_SQPP_SLUGS

export const SQPP_SLUG_MAP = SQPP_FLOW.reduce(
  (acc, row) => Object.assign(acc, { [row.slug]: row }),
  {}
)

export const SQPP_SLUG_PATTERN = ENERGY_SQPP_SLUG_PATTERN

/**
 * Static control-step -> slug map, used when there is no model to consult
 * (server-side middleware on a cold entry). `step1` is the only step a cold
 * entry uses, so the static answer is correct where it matters.
 *
 * `step5`/`step6` are the bill-upload screens, which the variant omits.
 */
export const CONTROL_STEP_TO_SLUG = {
  step1: 'what-to-compare',
  step2: 'moving',
  step3: 'home-or-business',
  step4: 'own-or-rent',
  step5: null,
  step6: null,
  step7: 'address',
  step8: null
}

/**
 * Inverse map, for sending a visitor back to the control arm when the SDK later
 * says they are explicitly variation 0.
 */
export const SLUG_TO_CONTROL_STEP = SQPP_FLOW.reduce((acc, row) => {
  return Object.assign(acc, { [row.slug]: row.step ?? 'details' })
}, {})

export const SQPP_ERROR_MAP = {
  fuel_type: 'Please select one option',
  account_type: 'Please select one option',
  sales_type: 'Please select one option',
  moving_date: 'Please select moving date',
  property_type: 'Please select one option',
  address: 'Please enter your address',
  postcode: 'Please enter your valid postcode',
  business_name: 'Please enter your business name',
  energy_usage_estimate: 'Please select one option',
  electricity_retailer: 'Please select the electricity retailer name',
  gas_retailer: 'Please select the gas retailer name',
  life_support: 'Please select one option',
  solar: 'Please select one option'
}

/**
 * The CRM sentinel written when a visitor takes `Not sure? Skip for now` on a
 * retailer screen. Empty string is what the control already sends when the
 * question is never asked, so it is the one value the CRM provably accepts.
 */
export const SQPP_RETAILER_SKIP_VALUE = ''

/**
 * The `[step, field]` pairs backed by `ProviderLogoSelection`, derived from the
 * flow so a moved row cannot silently drop out.
 *
 * The page normalises exactly these on the way into the store: the logo grid
 * writes the whole provider OBJECT to the model, while the dropdown underneath
 * writes the plain string — and `leadParams` passes `electricity_retailer` /
 * `gas_retailer` through RAW (`stores/energy.js`). Without the normalisation a
 * tile-picked lead reaches the CRM as `{ code, name, logo, dimensions }` where
 * control sends `'AGL'`, which would diverge the variant's retailer data for
 * the whole A/B.
 */
export const SQPP_RETAILER_FIELDS = SQPP_FLOW
  .filter(row => row.component === 'OrganismsProviderLogoSelection')
  .map(row => [row.step, row.field])
